import {
  IPerRoadScenarioImperial,
  PerRoadLayerType,
  PrAxleConfigurationType,
  PrRoadwayClassifications,
} from '@/modules/perRoad/interfaces/perRoad';
import {BaseScenarioType} from '@/lib/types/IBaseScenario';
import {UnitSystem} from '@/interfaces/units';
import {IProjectMetadata} from '@/interfaces/report';
import {getUserId} from '@/modules/design/lib/r23ToSuiteTransformers';
import {fall, spring, summer, winter} from "@/modules/perRoad/lib/layers/seasons";
import {
  createNewAcLayer,
  createNewGenericLayer,
  createNewGranularBase,
  newDisabledSoilLayer
} from "@/modules/perRoad/lib/layers/createNewLayer";
import {aadtDistByRoadClass, getNewLoadDistribution} from "@/modules/perRoad/lib/roadwayFunctionalClassifications";


/**
 * Scenario
 * @param project
 */

const defaultRoadwayClassification = PrRoadwayClassifications.URBAN_INTERSTATE;

export const createNewPerRoadScenario = function createNewPerRoadScenario(project: IProjectMetadata | null): IPerRoadScenarioImperial {
  const s: IPerRoadScenarioImperial = {
    id: null,
    user: getUserId(),
    name: 'Default Scenario',
    project: project ? project.id : -1,
    created: new Date(Date.now()).toISOString(),
    modified: new Date(Date.now()).toISOString(),
    visible: true,
    displayUnits: project ? project.displayUnits : UnitSystem.Imperial,
    unitSystem: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.perRoad,
    isComputedFromLegacy: false,

    description: null,

    district: null,
    county: null,
    route: null,

    seasons: [
      summer,
      fall,
      winter,
      spring,
    ],

    twoWayAadt: 40000, // Ballpark California interstate AADT average, 2011:
                       // https://www.fhwa.dot.gov/interstatebrief2011/
    axleGroupsPerDay: -1, // Calculated value
    pctTrucks: 10,
    pctTruckGrowthPerYear: 4,
    pctTrucksInDesignLane: 90,
    directionalDistribution: 50,

    /**
     * Standard axle type distributions by vehicle class. Steering axles not considered.
     */
    vehicleAxlesSingle: [1.62, 2, 1.02, 1, 2.38, 1.13, 1.19, 4.29, 3.52, 2.15],
    vehicleAxlesTandem: [0.39, 0, 0.99, 0.26, 0.67, 1.93, 1.09, 0.26, 1.14, 2.13],
    vehicleAxlesTridem: [0, 0, 0, 0.83, 0, 0, 0.89, 0.06, 0.06, 0.35],
    vehicleAxlesSteer: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],


    /**
     * Set default roadway configuraiton
     */
    vehiclesPctAadt: aadtDistByRoadClass[defaultRoadwayClassification],
    roadwayClassification: defaultRoadwayClassification,

    axleLoadDistSingle: getNewLoadDistribution(PrAxleConfigurationType.SINGLE, aadtDistByRoadClass[defaultRoadwayClassification]),
    axleLoadDistTandem: getNewLoadDistribution(PrAxleConfigurationType.TANDEM, aadtDistByRoadClass[defaultRoadwayClassification]),
    axleLoadDistTridem: getNewLoadDistribution(PrAxleConfigurationType.TRIDEM, aadtDistByRoadClass[defaultRoadwayClassification]),

    axleSingleEnabled: true,
    axleTandemEnabled: true,
    axleTridemEnabled: true,
    axleSteerEnabled: false,


    /**
     * Layers start empty, but we always need five.
     */
    layers: [],

  };

  /**
   * Set five layers
   */
  s.layers.push(createNewAcLayer(PerRoadLayerType.AC, s.seasons))
  s.layers.push(createNewGranularBase(PerRoadLayerType.GRANULAR_BASE, s.seasons))
  s.layers.push(createNewGenericLayer(PerRoadLayerType.SOIL, s.seasons))
  s.layers.push(newDisabledSoilLayer())
  s.layers.push(newDisabledSoilLayer())

  return s;
};
